import type { FC } from 'react';
import { useBrandingOptions } from '@features/create-entity-modal';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useModalDispatch } from '@hooks/useModalProvider';
import { useTags } from '@queries/fetchTags';
import { useAllTeams } from '@queries/fetchTeams';
import { useTemplates } from '@queries/fetchTemplates';
import type { Action, PlainAction } from '@root/@types/types';
import { TEMPLATE_REVIEW_STATUSES, TEMPLATE_STATUSES } from '@root/@types/types';
import { isNonNullish } from '@root/helpers';
import withMenuLayout from '@root/src/layout/withMenuLayout';
import { isPPLFrontend } from '@root/src/utils/app-paths';

import { MODALS } from '../../constants';

import { DownloadCSVButton } from './components/DownloadCSVButton';
import { getTemplatesTableColumns } from './helpers/columns/getTemplatesTableColumns';
import { ReactTable } from './components';
import { useDataIsEmpty } from './hooks';

export const Templates: FC = () => {
  const { showModal } = useModalDispatch();
  const { data = [], isLoading: isTemplatesLoading, status } = useTemplates();
  const { data: tags = [], isLoading: isTagsLoading } = useTags();
  const { data: teams = [], isLoading: isTeamsFetching } = useAllTeams();
  const getFeatureFlagState = useFeatureFlags();
  const templateStatuses = getFeatureFlagState('TemplateReviewWorkflow')
    ? [...TEMPLATE_REVIEW_STATUSES]
    : TEMPLATE_STATUSES;
  const isEmpty = useDataIsEmpty(data, status);
  const { brandingFieldName, data: brandingOptions = [] } = useBrandingOptions();

  const columns = getTemplatesTableColumns({ tags, teams, brandingFieldName, brandingOptions, templateStatuses });

  const createEntityAction: PlainAction = {
    key: 'create',
    label: 'New Template',
    onClick: () => {
      showModal(MODALS.ADMIN_CREATE_TEMPLATE, { data });
    },
  };

  const actions: Action[] = [
    createEntityAction,
    isPPLFrontend ? undefined : <DownloadCSVButton key="download-csv" type="templates" />,
  ].filter(isNonNullish);

  return (
    <ReactTable
      actions={actions}
      columns={columns}
      columnVisibility={{
        branding: false,
        class_of_business: false,
        created_at: false,
        created_by: false,
        has_default_content: false,
        id: false,
        last_updated_by: false,
      }}
      columnPinning={{ right: ['view'] }}
      createEntityAction={createEntityAction}
      entityName="template"
      filteredData={data}
      initialSorting={[{ id: 'created_at', desc: true }]}
      initialFilters={[{ id: 'status', value: templateStatuses.filter((status) => status !== 'ARCHIVED') }]}
      status={status}
      isEmpty={isEmpty}
      isFetching={isTeamsFetching}
      isLoading={isTemplatesLoading || isTagsLoading}
    />
  );
};

export const UserTemplatesPage = withMenuLayout(Templates);
